// extracted from @formatjs/intl-utils which doesn't exist anymore
import { IntlShape, PrimitiveType } from 'react-intl';

type TimeUnit =
  | 'second'
  | 'minute'
  | 'hour'
  | 'day'
  | 'week'
  | 'month'
  | 'year';

type Thresholds = Partial<{
  second: number;
  minute: number;
  hour: number;
  day: number;
}>;

interface SelectUnitResult {
  value: number;
  unit: TimeUnit;
}

export interface LinkParams {
  href: string;
  text: string;
}

const DEFAULT_THRESHOLDS: Required<Thresholds> = {
  second: 45,
  minute: 45,
  hour: 22,
  day: 5,
};

const MS_PER_SECOND = 1_000;
const SECS_PER_MIN = 60;
const SECS_PER_HOUR = SECS_PER_MIN * 60;
const SECS_PER_DAY = SECS_PER_HOUR * 24;
const SECS_PER_WEEK = SECS_PER_DAY * 7;

export function selectUnit(
  from: number | Date,
  to?: number | Date,
  thresholds?: Thresholds
): SelectUnitResult {
  const fromTime = typeof from === 'number' ? from : from.getTime();
  const toTime =
    typeof to === 'undefined'
      ? Date.now()
      : typeof to === 'number'
      ? to
      : to.getTime();

  const resolvedThresholds = { ...DEFAULT_THRESHOLDS, ...thresholds };
  const secs = (fromTime - toTime) / MS_PER_SECOND;

  if (Math.abs(secs) < resolvedThresholds.second) {
    return { value: Math.round(secs), unit: 'second' };
  }

  const mins = secs / SECS_PER_MIN;
  if (Math.abs(mins) < resolvedThresholds.minute) {
    return { value: Math.round(mins), unit: 'minute' };
  }

  const hours = secs / SECS_PER_HOUR;
  if (Math.abs(hours) < resolvedThresholds.hour) {
    return { value: Math.round(hours), unit: 'hour' };
  }

  const days = secs / SECS_PER_DAY;
  if (Math.abs(days) < resolvedThresholds.day) {
    return { value: Math.round(days), unit: 'day' };
  }

  const fromDate = new Date(fromTime);
  const toDate = new Date(toTime);
  const years = fromDate.getFullYear() - toDate.getFullYear();
  if (Math.round(Math.abs(years)) > 0) {
    return { value: Math.round(years), unit: 'year' };
  }

  const months = years * 12 + fromDate.getMonth() - toDate.getMonth();
  if (Math.round(Math.abs(months)) > 0) {
    return { value: Math.round(months), unit: 'month' };
  }

  const weeks = secs / SECS_PER_WEEK;
  return { value: Math.round(weeks), unit: 'week' };
}

export const generateTranslationKey = (namespace?: string) => (id: string) =>
  id.startsWith('.') && namespace ? `${namespace}${id}` : id;

// Gets date in format: YYYY-mm-dd
export const dateInDbFormat = (date: Date): string =>
  date.toISOString().substring(0, 10);

export const translate = (
  intl: IntlShape,
  id: string,
  opts?: Record<string, PrimitiveType>
): string => intl.formatMessage({ id }, opts);

export const generateLink = ({ href, text }: LinkParams): string =>
  `<a href="${href}" target="_blank" rel="noreferrer">${text}</a>`;
